import { APP_NAME } from '@/constants/common'
import { explorePageTabsIds } from '@/constants/explore'
import { goalsTabIds } from '@/constants/goals'
import { learnPageTabsIds } from '@/constants/learn'
import { ROUTE_NAMES } from '@/constants/routes'
import { addToHistory } from '@/history'
import * as keymapService from '@/services/keymapService/keymapService'
import getOrgInfo from '@/store/utils/getOrgInfo'
import getTeamInfo from '@/store/utils/getTeamInfo'
import getClosestOrgLikeTeam from '@/utils/getClosestOrgLikeTeam'
import getClosestOrgLikeTeamId from '@/utils/getClosestOrgLikeTeamId'
import getDocumentTitle from '@/utils/getDocumentTitle'
import ExploreNewGoal from '@/views/ExploreNewGoal.vue'
import SubOrgIdInjector from '@/views/SubOrgIdInjector'
import Vue from 'vue'
import Router from 'vue-router'
import store from '@/store/store'
import Goal from '@/views/Goal'
import Initiative from '@/views/Initiative'
import KR from '@/views/KR'
import OrgMain from '@/views/OrgMain'
import OrgSelect from '@/views/OrgSelect'
import Team from '@/views/Team'
import Project from '@/views/Project'
import YourWork from '@/views/YourWork'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/',
      name: ROUTE_NAMES.ORG_SELECT,
      component: OrgSelect,
      meta: {
        title: getDocumentTitle({ title: 'Your Orgs' })
      }
    },
    {
      path: '/new-goal',
      name: ROUTE_NAMES.NEW_GOAL_GLOBAL,
      component: ExploreNewGoal,
      meta: {
        title: getDocumentTitle({ title: 'Create OKRs with North AI' }),
        public: true
      },
      props: () => ({ nextRoute: ROUTE_NAMES.NEW_GOAL })
    },
    {
      path: '/orgs/:orgId/suborgs/:subOrgId',
      component: OrgMain,
      props(route) {
        return {
          orgId: +route.params.orgId,
          subOrgId: +route.params.subOrgId
        }
      },
      children: [
        {
          path: '',
          name: ROUTE_NAMES.ORG_MAIN_HOME,
          props: true,
          redirect: {
            name: ROUTE_NAMES.YOUR_WORK
          }
        },
        {
          path: 'user-goals',
          name: ROUTE_NAMES.YOUR_WORK,
          component: YourWork,
          props(route) {
            return {
              orgId: +route.params.orgId,
              subOrgId: +route.params.subOrgId,
              query: route.query
            }
          },
          meta: {
            title(to) {
              return getTeamInfo(to.params.subOrgId).then(team =>
                getDocumentTitle({ teamName: team.name, title: 'Your work' })
              )
            }
          }
        },
        {
          path: 'all-goals',
          name: ROUTE_NAMES.ALL_GOALS,
          props(route) {
            return {
              orgId: +route.params.orgId,
              subOrgId: +route.params.subOrgId,
              query: route.query
            }
          },
          beforeEnter(to, from, next) {
            next({
              name: ROUTE_NAMES.EXPLORE_ALL,
              params: {
                ...to.params,
                entity: explorePageTabsIds.goals
              },
              query: to.query
            })
          }
        },
        {
          path: 'explore/:entity?',
          name: ROUTE_NAMES.EXPLORE_ALL,
          component: () => import(/* webpackChunkName: "explore-all" */ '../views/ExploreAll.vue'),
          props(route) {
            return {
              orgId: +route.params.orgId,
              entity: route.params.entity || explorePageTabsIds.goals,
              subOrgId: +route.params.subOrgId,
              query: route.query
            }
          },
          meta: {
            title({ params }) {
              const getTitle = teamInfo => {
                let teamName = teamInfo.name
                switch (params.entity) {
                  case explorePageTabsIds.goals:
                    return getDocumentTitle({ teamName, title: 'Explore all Goals' })
                  case explorePageTabsIds.initiatives:
                    return getDocumentTitle({ teamName, title: 'Explore all Initiatives' })
                  case explorePageTabsIds.activity:
                    return getDocumentTitle({ teamName, title: 'Explore all Activities' })
                  default:
                    return getDocumentTitle({ teamName, title: 'Explore' })
                }
              }
              return getTeamInfo(params.subOrgId).then(getTitle)
            }
          }
        },
        {
          path: 'lists/:entity/new',
          name: ROUTE_NAMES.LISTS,
          component: () => import(/* webpackChunkName: "explore-all" */ '../views/Lists.vue'),
          props(route) {
            return {
              orgId: +route.params.orgId,
              subOrgId: +route.params.subOrgId,
              entity: route.params.entity,
              query: route.query
            }
          },
          meta: {
            title({ params }) {
              return getTeamInfo(params.subOrgId).then(team =>
                getDocumentTitle({ teamName: team.name, title: 'Create a list' })
              )
            }
          }
        },
        {
          path: 'all-initiatives',
          name: ROUTE_NAMES.ALL_INITIATIVES,
          props(route) {
            return {
              orgId: +route.params.orgId,
              subOrgId: +route.params.subOrgId
            }
          },
          beforeEnter(to, from, next) {
            next({
              name: ROUTE_NAMES.EXPLORE_ALL,
              params: {
                ...to.params,
                entity: explorePageTabsIds.initiatives
              },
              query: to.query
            })
          }
        },
        {
          path: 'all-activity',
          name: ROUTE_NAMES.ALL_ACTIVITY,
          props(route) {
            return {
              orgId: +route.params.orgId,
              subOrgId: +route.params.subOrgId
            }
          },
          beforeEnter(to, from, next) {
            next({
              name: ROUTE_NAMES.EXPLORE_ALL,
              params: {
                ...to.params,
                entity: explorePageTabsIds.activity
              },
              query: to.query
            })
          }
        },
        {
          path: 'goals/new',
          redirect: {
            name: ROUTE_NAMES.NEW_GOAL
          }
        },
        {
          path: 'goals/:goalId',
          children: [
            {
              path: '',
              redirect: {
                name: ROUTE_NAMES.GOAL
              }
            },
            {
              path: 'krs/:krId',
              redirect: {
                name: ROUTE_NAMES.KR
              }
            },
            {
              path: 'krs/:krId/initiatives/:initiativeId',
              name: ROUTE_NAMES.INITIATIVE_OLD,
              redirect: {
                name: ROUTE_NAMES.INITIATIVE
              }
            }
          ]
        },
        {
          path: 'goals/:goalId/edit',
          redirect: {
            name: ROUTE_NAMES.EDIT_GOAL
          }
        },
        {
          path: 'initiatives/:initiativeId',
          redirect: {
            name: ROUTE_NAMES.INITIATIVE
          }
        },
        {
          path: 'idea-lists/:listId',
          redirect: {
            name: ROUTE_NAMES.IDEA_LIST
          }
        },
        {
          path: 'ideas/:ideaId',
          redirect: {
            name: ROUTE_NAMES.IDEA
          }
        },
        {
          path: 'archive',
          name: ROUTE_NAMES.ARCHIVE,
          component: () => import(/* webpackChunkName: "archive" */ '../views/Archive.vue'),
          props: route => ({
            section: route.query.section,
            orgId: +route.params.orgId,
            subOrgId: +route.params.subOrgId
          }),
          meta: {
            title(to) {
              return getTeamInfo(to.params.subOrgId).then(team =>
                getDocumentTitle({ teamName: team.name, title: 'Archive' })
              )
            }
          }
        },
        {
          path: 'users/:userId',
          name: ROUTE_NAMES.USER,
          component: YourWork,
          props(route) {
            return {
              orgId: +route.params.orgId,
              subOrgId: +route.params.subOrgId,
              userId: +route.params.userId,
              query: route.query
            }
          },
          meta: {
            title: getDocumentTitle({ title: 'User' })
          }
        },
        {
          path: 'admin',
          name: ROUTE_NAMES.ADMIN_PANEL,
          component: () => import(/* webpackChunkName: "admin-panel" */ '../views/AdminPanel.vue'),
          props(route) {
            return {
              orgId: +route.params.orgId,
              subOrgId: +route.params.subOrgId
            }
          },
          meta: {
            title(to) {
              return getTeamInfo(to.params.subOrgId).then(team =>
                getDocumentTitle({ teamName: team.name, title: 'Admin Panel' })
              )
            }
          }
        },
        {
          path: 'teams/:teamId/:entity?',
          props: true,
          redirect: {
            name: ROUTE_NAMES.TEAM
          }
        }
      ]
    },
    {
      path: '/orgs/:orgId/',
      name: ROUTE_NAMES.ORG_BASE,
      component: OrgMain,
      props(route) {
        return {
          orgId: +route.params.orgId,
          teamId: +route.params.teamId
        }
      },
      children: [
        {
          path: 'teams/:teamId/:entity?',
          name: ROUTE_NAMES.TEAM,
          component: Team,
          props(route) {
            return {
              orgId: +route.params.orgId,
              teamId: +route.params.teamId,
              entity: route.params.entity,
              query: route.query
            }
          },
          meta: {
            title(to) {
              const getTitle = teamInfo => {
                let teamName = teamInfo.name
                const orgTeamId = getClosestOrgLikeTeamId(store, teamInfo.id)
                const orgName = store.getters.teamById(orgTeamId).name
                if (teamInfo.id === orgTeamId) {
                  teamName = ''
                }
                switch (to.params.entity) {
                  case goalsTabIds.vision:
                    return getDocumentTitle({ orgName, teamName, title: 'Vision' })
                  case goalsTabIds.initiatives:
                    return getDocumentTitle({ orgName, teamName, title: 'Initiatives' })
                  case goalsTabIds.activity:
                    return getDocumentTitle({ orgName, teamName, title: 'Activity' })
                  case goalsTabIds.ideas:
                    return getDocumentTitle({ orgName, teamName, title: 'Ideas' })
                  case goalsTabIds.goals:
                    return getDocumentTitle({ orgName, teamName, title: 'Goals' })
                  default:
                    return getDocumentTitle({ orgName, teamName })
                }
              }
              return getTeamInfo(to.params.teamId).then(getTitle)
            }
          }
        },
        {
          path: 'projects/:projectId',
          name: ROUTE_NAMES.PROJECT,
          component: Project,
          props(route) {
            return {
              orgId: +route.params.orgId,
              projectId: +route.params.projectId,
              query: route.query
            }
          }
        },
        {
          path: 'goals',
          redirect: to => {
            const params = to.params
            const query = to.query
            if (query.type == 'team' && query.teamId) {
              return {
                name: ROUTE_NAMES.TEAM,
                params: { orgId: params.orgId, teamId: query.teamId },
                query: null
              }
            } else {
              return {
                name: ROUTE_NAMES.ORG_BASE,
                params: { orgId: params.orgId },
                query: null
              }
            }
          }
        },
        {
          path: 'goals/new',
          name: ROUTE_NAMES.NEW_GOAL,
          component: () => import(/* webpackChunkName: "new-goal" */ '../views/NewGoal.vue'),
          props(route) {
            return {
              orgId: +route.params.orgId,
              teamId: +route.query.teamId,
              expanded: !!route.query.expanded,
              useTemplate: !!route.query.useTemplate
            }
          },
          meta: {
            title(to) {
              if (to.query.teamId) {
                return getTeamInfo(to.query.teamId).then(team => {
                  const org = getClosestOrgLikeTeam(store, to.query.teamId)
                  let teamName = team.name
                  if (org.id === team.id) {
                    teamName = ''
                  }
                  return getDocumentTitle({ orgName: org.name, teamName, title: 'Create goal' })
                })
              }
              return getOrgInfo(to.params.orgId).then(org =>
                getDocumentTitle({ orgName: org.name, title: 'Create goal' })
              )
            }
          }
        },
        {
          path: 'goals/:goalId',
          name: ROUTE_NAMES.GOAL,
          component: Goal,
          props: route => ({
            krId: +route.query.krId,
            goalId: +route.params.goalId,
            orgId: +route.params.orgId
          })
        },
        {
          path: 'goals/:goalId/edit',
          name: ROUTE_NAMES.EDIT_GOAL,
          component: () => import(/* webpackChunkName: "edit-goal" */ '../views/EditGoal.vue'),
          props(route) {
            return {
              orgId: +route.params.orgId,
              goalId: +route.params.goalId,
              krId: route.query.kr,
              section: route.query.section,
              action: route.query.action
            }
          },
          meta: {
            title(to) {
              return getOrgInfo(to.params.orgId).then(org =>
                getDocumentTitle({ orgName: org.name, title: 'Edit Goal' })
              )
            }
          }
        },
        {
          path: 'krs/:krId',
          name: ROUTE_NAMES.KR,
          component: KR,
          props: route => ({
            krId: +route.params.krId,
            orgId: +route.params.orgId
          })
        },
        {
          path: 'initiatives/:initiativeId',
          name: ROUTE_NAMES.INITIATIVE,
          component: Initiative,
          props: route => ({
            orgId: +route.params.orgId,
            initiativeId: +route.params.initiativeId
          })
        },
        {
          path: 'idea-lists/:listId',
          name: ROUTE_NAMES.IDEA_LIST,
          component: () => import(/* webpackChunkName: "idea-list" */ '../views/IdeaList.vue'),
          props: route => ({
            orgId: +route.params.orgId,
            listId: +route.params.listId
          }),
          meta: {
            title(to) {
              return getOrgInfo(to.params.orgId).then(org =>
                getDocumentTitle({ orgName: org.name, title: 'Idea List' })
              )
            }
          }
        },
        {
          path: 'ideas/:ideaId',
          name: ROUTE_NAMES.IDEA,
          component: () => import(/* webpackChunkName: "idea" */ '../views/Idea.vue'),
          props: route => ({
            orgId: +route.params.orgId,
            ideaId: +route.params.ideaId
          }),
          meta: {
            title(to) {
              return getOrgInfo(to.params.orgId).then(org =>
                getDocumentTitle({ orgName: org.name, title: 'Idea' })
              )
            }
          }
        },
        {
          path: 'inbox',
          name: ROUTE_NAMES.INBOX,
          component: () => import(/* webpackChunkName: "inbox" */ '../views/Inbox.vue'),
          props: true,
          meta: {
            title(to) {
              return getOrgInfo(to.params.orgId).then(org =>
                getDocumentTitle({ orgName: org.name, title: 'Inbox' })
              )
            }
          }
        },
        {
          path: 'learn/:entity?',
          name: ROUTE_NAMES.NORTH_UNI,
          component: () => import(/* webpackChunkName: "north-uni" */ '../views/NorthUni.vue'),
          props(route) {
            return {
              orgId: +route.params.orgId,
              entity: route.params.entity || learnPageTabsIds.videos,
              query: route.query
            }
          },
          meta: {
            title({ params }) {
              const getTitle = orgInfo => {
                let orgName = orgInfo.name
                switch (params.entity) {
                  case learnPageTabsIds.howNorthWorks:
                    return getDocumentTitle({ orgName, title: 'How North works?' })
                  case learnPageTabsIds.videos:
                    return getDocumentTitle({ orgName, title: 'Essential videos' })
                  case learnPageTabsIds.goalTemplates:
                    return getDocumentTitle({ orgName, title: 'Goal templates' })
                  default:
                    return getDocumentTitle({ orgName, title: 'Learn' })
                }
              }
              return getOrgInfo(params.orgId).then(getTitle)
            }
          }
        },
        {
          path: 'notifications',
          name: ROUTE_NAMES.NOTIFICATIONS,
          component: () =>
            import(/* webpackChunkName: "notifications" */ '../views/Notifications.vue'),
          props(route) {
            return {
              orgId: +route.params.orgId
            }
          },
          meta: {
            title(to) {
              return getOrgInfo(to.params.orgId).then(org =>
                getDocumentTitle({ orgName: org.name, title: 'Your Notifications' })
              )
            }
          }
        },
        {
          path: 'api',
          name: ROUTE_NAMES.API,
          component: () => import(/* webpackChunkName: "api" */ '../views/API.vue'),
          props(route) {
            return {
              orgId: +route.params.orgId
            }
          },
          meta: {
            title(to) {
              return getOrgInfo(to.params.orgId).then(org =>
                getDocumentTitle({ orgName: org.name, title: 'API' })
              )
            }
          }
        },
        { path: '*', props: true, name: ROUTE_NAMES.ORG_OLD_ROUTE, component: SubOrgIdInjector }
      ]
    },
    {
      path: '/login',
      name: ROUTE_NAMES.LOGIN,
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "login" */ '../views/Login.vue'),
      meta: {
        public: true,
        title: `Login - ${APP_NAME}`
      }
    },
    {
      path: '/logout',
      name: ROUTE_NAMES.LOGOUT,
      component: () => import(/* webpackChunkName: "logout" */ '../views/Logout.vue'),
      meta: {
        noLoginRedirect: true
      }
    },
    {
      path: '/forgot-password',
      name: ROUTE_NAMES.FORGOT_PASSWORD,
      component: () =>
        import(/* webpackChunkName: "forgot-password" */ '../views/ForgotPassword.vue'),
      meta: {
        public: true,
        title: `Forgot Password - ${APP_NAME}`
      }
    },
    {
      path: '/reset-password',
      name: ROUTE_NAMES.RESET_PASSWORD,
      component: () =>
        import(/* webpackChunkName: "reset-password" */ '../views/ResetPassword.vue'),
      props(route) {
        return {
          email: route.query.email,
          token: route.query.token
        }
      },
      meta: {
        public: true,
        title: `Reset Password - ${APP_NAME}`
      }
    },
    {
      path: '/invitation-sign-up',
      name: ROUTE_NAMES.INVITATION_SIGN_UP,
      component: () =>
        import(/* webpackChunkName: "onboarding" */ '../views/onboarding/SetPasswordOnInvite.vue'),
      meta: {
        public: true,
        title: `Sign Up - ${APP_NAME}`
      }
    },
    {
      path: '/sign-up',
      name: ROUTE_NAMES.SIGN_UP,
      component: () =>
        import(/* webpackChunkName: "onboarding" */ '../views/onboarding/SignUp.vue'),
      meta: {
        public: true,
        title: `Sign Up - ${APP_NAME}`
      }
    },
    {
      path: '/create-org',
      name: ROUTE_NAMES.CREATE_ORG,
      component: () =>
        import(/* webpackChunkName: "onboarding" */ '../views/onboarding/CreateOrg.vue'),
      meta: {
        title: `Create workspace - ${APP_NAME}`
      }
    },
    {
      path: '/collect-reference',
      name: ROUTE_NAMES.COLLECT_REFERENCE,
      component: () =>
        import(/* webpackChunkName: "onboarding" */ '../views/onboarding/CollectReferral.vue'),
      meta: {
        title: `Create workspace - ${APP_NAME}`
      }
    },
    {
      path: '/join-org',
      name: ROUTE_NAMES.JOIN_ORG,
      component: () =>
        import(/* webpackChunkName: "onboarding" */ '../views/onboarding/AutoJoinOrgs.vue'),
      meta: {
        title: `Join workspace - ${APP_NAME}`
      }
    },
    {
      path: '/invite/:orgId',
      props: true,
      name: ROUTE_NAMES.INVITE_TO_ORG,
      component: () =>
        import(/* webpackChunkName: "onboarding" */ '../views/onboarding/InviteToOrg.vue'),
      meta: {
        title: `Invite Users - ${APP_NAME}`
      }
    },
    {
      path: '/set-profile',
      name: ROUTE_NAMES.SET_PROFILE,
      component: () =>
        import(/* webpackChunkName: "onboarding" */ '../views/onboarding/SetProfile.vue'),
      meta: {
        title: `Set Profile - ${APP_NAME}`
      }
    },
    {
      path: '/invitation',
      name: ROUTE_NAMES.INVITATION,
      component: () =>
        import(/* webpackChunkName: "onboarding" */ '../views/onboarding/Invitation.vue'),
      props(route) {
        return {
          invitationKey: route.query.key
        }
      },
      meta: {
        public: true,
        title: `Invitation - ${APP_NAME}`
      }
    },
    {
      path: '/set-password',
      name: ROUTE_NAMES.SET_PASSWORD,
      component: () =>
        import(/* webpackChunkName: "onboarding" */ '../views/onboarding/SetPasswordOnSignUp.vue'),
      meta: {
        title: `Set Password - ${APP_NAME}`
      }
    },
    {
      path: '/how-north-works/:step?',
      name: ROUTE_NAMES.HOW_NORTH_WORKS,
      component: () =>
        import(/* webpackChunkName: "onboarding" */ '../views/onboarding/OnboardingHowNorthWorks.vue'),
      props(route) {
        return {
          step: isNaN(+route.params.step) ? '' : +route.params.step
        }
      },
      meta: {
        title: `How North works - ${APP_NAME}`
      }
    },
    {
      path: '/request-invite',
      name: ROUTE_NAMES.REQUEST_INVITE,
      component: () =>
        import(/* webpackChunkName: "onboarding" */ '../views/onboarding/RequestInvite.vue'),
      meta: {
        title: getDocumentTitle({ title: 'Request invite' })
      }
    },
    {
      path: '/mywork',
      name: ROUTE_NAMES.MY_WORK,
      component: () => import(/* webpackChunkName: "my-work" */ '../views/MyWork.vue'),
      meta: {
        title: `My work - ${APP_NAME}`
      }
    },
    {
      path: '/doorway',
      name: ROUTE_NAMES.DOORWAY,
      component: () => import(/* webpackChunkName: "doorway" */ '../views/Doorway.vue'),
      meta: {
        public: true,
        title: getDocumentTitle()
      }
    },
    {
      path: '/authorize-google',
      name: ROUTE_NAMES.AUTHORIZE_GOOGLE,
      component: () =>
        import(/* webpackChunkName: "google-authorizer" */ '../views/GoogleAuthorizer.vue'),
      meta: {
        title: 'Authorize Google'
      }
    },
    {
      path: '/404',
      alias: '*',
      name: ROUTE_NAMES.NOT_FOUND,
      component: () => import(/* webpackChunkName: "page-not-found" */ '../views/PageNotFound.vue'),
      meta: {
        public: true,
        title: `Page Not Found - ${APP_NAME}`
      }
    }
  ]
})

// For handling of views with public: true, take a look at App.vue

async function updateTitle(to) {
  let title
  const defaultTitle = 'Your North'
  if (typeof to.meta.title === 'function') {
    try {
      title = await to.meta.title(to)
    } catch {
      title = defaultTitle
    }
  } else {
    title = to.meta.title
  }
  if (title) {
    setTimeout(() => (document.title = title), 0)
  }
}

router.beforeEach(async (to, from, next) => {
  store.commit('VISIT_ROUTE', to)
  updateTitle(to).finally(next)
})

router.afterEach(route => {
  keymapService.removeEventsOfType('MODAL_SHOWN')
  keymapService.removeEventsOfType('POPOVER_SHOWN')
  addToHistory(route)
})

window.onpopstate = () => {
  store.commit('REMOVE_POPPED_ROUTE')
}

export default router
